import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  refreshAdminBadges,
  subscribeAdminBadges,
} from "../../services/adminBadgeService";
import "./AdminLayout.css";

const STROKE = 1.8;

function EyeLogo({ size = 28 }) {
  return (
    <svg
      width={size}
      height={Math.round((size * 20) / 28)}
      viewBox="0 0 112 48"
      fill="none"
      aria-hidden="true"
      className="admin-eye-logo"
    >
      <path
        d="M0 24 C14 5 38 0 56 0 C74 0 98 5 112 24 C98 43 74 48 56 48 C38 48 14 43 0 24Z"
        stroke="#C4790A"
        strokeWidth="4"
        fill="none"
      />
      <circle cx="56" cy="24" r="13" fill="#FDF3E0" />
      <circle cx="56" cy="24" r="8" fill="#C4790A" />
      <circle cx="59.5" cy="20.5" r="2.5" fill="white" opacity="0.95" />
    </svg>
  );
}

function NavIcon({ children }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const TOOL_LINKS = [
  {
    to: "/admin/approvals/creators",
    label: "Creator Approvals",
    badgeKey: "pendingCreators",
    badgeTone: "red",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M8 12.5l2.5 2.5L16 9.5" />
      </>
    ),
  },
  {
    to: "/admin/approvals/fans",
    label: "Fan Waitlist",
    badgeKey: "waitlistedFans",
    badgeTone: "red",
    icon: (
      <>
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </>
    ),
  },
  {
    to: "/admin/support",
    label: "Support Inbox",
    badgeKey: "openTickets",
    badgeTone: "orange",
    icon: (
      <>
        <path d="M4 6h16v12H4z" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
  },
  {
    to: "/admin/games",
    label: "Games",
    icon: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
  },
  {
    to: "/admin/comment-reports",
    label: "Comment Reports",
    badgeKey: "pendingCommentReports",
    badgeTone: "red",
    icon: (
      <>
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </>
    ),
  },
  {
    to: "/admin/suggestions",
    label: "Suggestions",
    badgeKey: "pendingSuggestions",
    badgeTone: "gold",
    icon: (
      <>
        <path d="M8 12h.01M12 12h.01M16 12h.01" />
        <path d="M21 12c0 4.4-4 8-9 8a10 10 0 0 1-3-.4L3 21l1.4-4A7.9 7.9 0 0 1 3 12c0-4.4 4-8 9-8s9 3.6 9 8z" />
      </>
    ),
  },
  {
    to: "/admin-reports",
    label: "Content Reports",
    badgeTone: "red",
    icon: (
      <>
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
        <circle cx="18" cy="6" r="2" />
      </>
    ),
  },
  {
    to: "/admin/email-templates",
    label: "Email Templates",
    icon: (
      <>
        <path d="M4 5h16v14H4z" />
        <path d="m4 7 8 6 8-6" />
        <path d="M8 15h5" />
      </>
    ),
  },
  {
    to: "/admin/outreach",
    label: "Outreach Drafts",
    icon: (
      <>
        <path d="M22 2L11 13" />
        <path d="M22 2l-7 20-4-9-9-4 20-7z" />
      </>
    ),
  },
  {
    to: "/admin/users",
    label: "Users",
    icon: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </>
    ),
  },
  {
    to: "/admin/settings",
    label: "Settings",
    icon: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </>
    ),
  },
];

function isActivePath(pathname, to) {
  if (pathname === to) return true;
  if (to === "/admin") return pathname === "/admin";
  return pathname.startsWith(`${to}/`);
}

function avatarInitial(profile, user) {
  const name =
    profile?.displayName ||
    profile?.handle ||
    user?.displayName ||
    user?.email ||
    "A";
  return String(name).trim().charAt(0).toUpperCase() || "A";
}

export default function AdminLayout({ children }) {
  const location = useLocation();
  const { currentUser, userProfile } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [badges, setBadges] = useState({
    pendingCreators: 0,
    waitlistedFans: 0,
    openTickets: 0,
    highPriorityTickets: 0,
    pendingCommentReports: 0,
  });

  useEffect(() => {
    const unsubscribe = subscribeAdminBadges(setBadges);
    return () => unsubscribe();
  }, []);

  // Refresh badge counts on every admin route change
  useEffect(() => {
    refreshAdminBadges();
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  const avatarUrl =
    userProfile?.avatar ||
    userProfile?.avatarUrl ||
    currentUser?.photoURL ||
    "";
  const initial = useMemo(
    () => avatarInitial(userProfile, currentUser),
    [userProfile, currentUser]
  );

  const sidebar = (
    <>
      <div className="sb-label">Admin Tools</div>
      <nav className="admin-sidebar-nav" aria-label="Admin tools">
        {TOOL_LINKS.map((link) => {
          const active = isActivePath(location.pathname, link.to);
          const count = link.badgeKey ? badges[link.badgeKey] || 0 : 0;
          return (
            <Link
              key={link.to}
              to={link.to}
              className={`sb-item${active ? " active" : ""}`}
              onClick={() => setMobileNavOpen(false)}
            >
              <NavIcon>{link.icon}</NavIcon>
              <span className="sb-item-label">{link.label}</span>
              {count > 0 && (
                <span className={`sb-badge sbb-${link.badgeTone || "red"}`}>
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
      <div className="admin-sidebar-foot">
        <Link to="/" className="admin-sidebar-back">
          Back to Platform
        </Link>
      </div>
    </>
  );

  return (
    <div className="admin-page">
      <header className="admin-topnav">
        <div className="admin-topnav-left">
          <button
            type="button"
            className="admin-hamburger"
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <NavIcon>
              {mobileNavOpen ? (
                <>
                  <path d="M6 6l12 12" />
                  <path d="M18 6L6 18" />
                </>
              ) : (
                <>
                  <path d="M4 7h16" />
                  <path d="M4 12h16" />
                  <path d="M4 17h16" />
                </>
              )}
            </NavIcon>
          </button>

          <Link to="/admin" className="admin-nav-brand">
            <EyeLogo />
            <div className="admin-nav-brand-text">
              <div className="admin-nav-wordmark">100ViewsOnly</div>
              <div className="admin-nav-tagline">Canada&apos;s Creator Platform 🍁</div>
            </div>
          </Link>
        </div>

        <div className="admin-topnav-centre" aria-hidden="true" />

        <div className="admin-topnav-right">
          <span className="admin-nav-badge">Admin</span>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="admin-btn-view-live"
          >
            View Live Site
          </a>
          <div className="admin-nav-avatar" title={userProfile?.displayName || "Admin"}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" />
            ) : (
              <span>{initial}</span>
            )}
          </div>
        </div>
      </header>

      <div className="admin-body">
        <aside className="admin-sidebar admin-sidebar-desktop">{sidebar}</aside>

        {mobileNavOpen && (
          <button
            type="button"
            className="admin-sidebar-backdrop"
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        <aside
          className={`admin-sidebar admin-sidebar-drawer${mobileNavOpen ? " open" : ""}`}
          aria-hidden={!mobileNavOpen}
        >
          {sidebar}
        </aside>

        <main className="admin-main">{children}</main>
      </div>
    </div>
  );
}
